import { LoggerService } from '@Shared/logger';
import { PAGE_SUITE_TAG_LIST_KEY } from '../_models';

function normalizeTag(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function normalizeTagList(value: unknown): string[] {
  return Array.isArray(value) ? value.map(normalizeTag).filter(Boolean) : [];
}

export class PageSuiteTagUtils {
  private static Logger = LoggerService.createLogger('PageSuiteTagUtils');

  /**
   * Reports whether the content PageSuite is currently showing carries
   * `targetTag`, so paywall rules can suppress the paywall or run a custom
   * experience.
   *
   * Matching ignores case and surrounding whitespace. Anything unreadable
   * yields `false` — never a throw — so a malformed payload can't stop paywall
   * execution.
   */
  static hasContentTag(targetTag: string): boolean {
    try {
      const target = normalizeTag(targetTag);
      if (!target) {
        this.Logger.warn('No target tag provided, returning false', targetTag);
        return false;
      }

      const pageData = this.readActivePageData();
      if (pageData) {
        this.Logger.trace('Active page data', pageData);
      }

      const tags = normalizeTagList(pageData?.[PAGE_SUITE_TAG_LIST_KEY]);
      const hasTag = tags.includes(target);

      if (hasTag) {
        this.Logger.info('Paywall exception tag found', target, tags);
      } else {
        this.Logger.trace('Paywall exception tag not found', target, tags);
      }

      return hasTag;
    } catch (error) {
      this.Logger.error('Error evaluating content tag', error);
      return false;
    }
  }

  /** The reader only exposes psReturnActivePageData once an edition is open. */
  private static readActivePageData(): Record<string, unknown> | null {
    const readActivePageData = window['psReturnActivePageData'];
    if (typeof readActivePageData !== 'function') {
      this.Logger.trace('psReturnActivePageData is not available yet');
      return null;
    }

    const pageData = readActivePageData();
    return typeof pageData === 'object' && pageData !== null ? pageData : null;
  }
}
